import { configFromDataset, sanitizeConfig } from "./lib/config";
import type { PlayerConfig } from "./lib/types";

declare global {
  interface Window {
    __DJ_PLAYER_DISABLE_AUTO__?: boolean;
  }
}

const cssLoaded = new Set<string>();
const moduleCache = new Map<string, Promise<unknown>>();
const moduleLoaders: Record<string, () => Promise<Record<string, unknown>>> = {
  full: () => import("./entry-full.tsx"),
  nowplaying: () => import("./entry-nowplaying.tsx"),
  history: () => import("./entry-history.tsx"),
  hero: () => import("./entry-hero.tsx"),
};

window.__DJ_PLAYER_DISABLE_AUTO__ = true;

function ensureStylesheet(href?: string) {
  if (!href || cssLoaded.has(href)) return;
  cssLoaded.add(href);
  if (document.querySelector(`link[rel="stylesheet"][href="${href}"]`)) return;
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = href;
  link.crossOrigin = "anonymous";
  document.head.appendChild(link);
}

function loadModule(src: string) {
  if (!moduleCache.has(src)) {
    moduleCache.set(src, import(/* @vite-ignore */ src));
  }
  return moduleCache.get(src)!;
}

async function mountElement(container: HTMLElement) {
  if (container.dataset.djMounted === "1") return;

  const type = container.dataset.djPlayer || "full";
  const jsSrc = container.dataset.djJs;
  const cssHref = container.dataset.djCss;
  const exportName = container.dataset.djExport || determineExport(container);
  const rawConfig = configFromDataset(container);

  if (cssHref) {
    ensureStylesheet(cssHref);
  }

  try {
    const module: Record<string, unknown> = jsSrc
      ? ((await loadModule(jsSrc)) as Record<string, unknown>)
      : await loadFromBundle(type);
    const mountFn = module[exportName];
    if (typeof mountFn !== "function") {
      console.error(`[dj-player] Export '${exportName}' bestaat niet voor type '${type}'`);
      return;
    }

    const config = sanitizeConfig(rawConfig as Partial<PlayerConfig>);
    (mountFn as (container: HTMLElement, config: Partial<PlayerConfig>) => void)(container, config);
  } catch (error) {
    console.error("[dj-player] Kon module niet laden:", type, error);
  }
}

function determineExport(container: HTMLElement): string {
  const type = container.dataset.djPlayer;
  if (type === "history") return "mountHistoryList";
  if (type === "nowplaying") return "mountNowPlaying";
  if (type === "hero") return "mountHeroPlayer";
  return "mountFullPlayer";
}

function bootstrap() {
  const containers = Array.from(
    document.querySelectorAll<HTMLElement>("[data-dj-player]"),
  );
  if (containers.length === 0) return;
  containers.forEach((el) => {
    void mountElement(el);
  });
}

async function loadFromBundle(type: string) {
  const loader = moduleLoaders[type];
  if (!loader) {
    throw new Error(`Onbekend player-type '${type}'`);
  }
  return loader();
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", bootstrap, { once: true });
} else {
  bootstrap();
}

export {};
